import { useMutation } from '@apollo/client';
import {
  Typography,
} from '@mui/material';
import React, {
  ChangeEvent,
  FormEvent,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { UPDATE_USER } from '../graphql';
import SimpleInput from '../../common/inputs/simpleInput';
import { InputType } from '../../common/inputs/utils/inputType';
import { ovAnalyticsEvents, sendAnalyticsEvent } from '../../../utils/firebase';
import OvForm from '../../common/wrappers/ovForm';

export interface UserState {
  totalDebtCents?: number,
  incompleteFields?: string[],
}

interface Props {
  user: UserState,
  updateUserState: (att: UserState) => void,
  onContinue?: () => void,
}

const ValueOfDebt = ({ user, updateUserState, ...props }: Props): JSX.Element => {
  const { t } = useTranslation(['base', 'user']);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(user.totalDebtCents ? (user.totalDebtCents / 100).toFixed(2) : '');
  const updateTotalDebtCentsOnCompleted = (data: { updateUser: { user: { incompleteFields: string[] } } }): void => {
    console.log({ event: 'TOTAL_DEBT_CENTS_UPDATED', totalDebtCents: Math.round(Number(value) * 100) });
    updateUserState({ ...user, incompleteFields: data.updateUser.user.incompleteFields });
    setLoading(false);
    if (props.onContinue) props.onContinue();
  };

  const [updateTotalDebtCentsStatus] = useMutation(UPDATE_USER, {
    variables: {
      input: { totalDebtCents: Math.round(Number(value) * 100) },
    },
    onCompleted: updateTotalDebtCentsOnCompleted,
    onError: () => setLoading(false),
  });

  const onContinueClick = (event : FormEvent<HTMLFormElement> | undefined): void => {
    event?.preventDefault();
    sendAnalyticsEvent(ovAnalyticsEvents.onBoardingEnterTotalDebt).then();
    setLoading(true);
    updateTotalDebtCentsStatus().then();
  };

  const updateState = (event: ChangeEvent<HTMLTextAreaElement|HTMLInputElement>): void => {
    let valueOfSavings;
    if (Number(event.target.value) > 90000000000000) {
      valueOfSavings = '90000000000000';
    } else {
      valueOfSavings = event.target.value;
    }
    setValue(valueOfSavings);
    updateUserState({ ...user, totalDebtCents: Math.round(Number(valueOfSavings) * 100) });
  };

  const clear = (): void => {
    setValue('');
  };

  return (
    <OvForm loading={loading} onSubmit={onContinueClick}>
      <Typography variant="heading2">{t('user:howToInvest.debtValue.title')}</Typography>
      <Typography variant="paragraph3">{t('user:howToInvest.debtValue.subTitle')}</Typography>
      <SimpleInput inputProps={{ inputMode: 'numeric' }} onClear={clear} label={t('user:howToInvest.annualIncome.valuePrompt')} testId="value-input" required inputType={InputType.CURRENCY} value={value} onChange={updateState} />
    </OvForm>
  );
};

ValueOfDebt.defaultProps = {
  onContinue: undefined,
};

export default ValueOfDebt;
